import { baseApi } from "../../../../app/api";
import { store } from "../../../../app/store";
import {
  RawWPRestProductCategory,
  RawWPRestProductCategoriesResponse,
  RawWPRestProductTag,
  RawWPRestProductTagsResponse,
  RawWPRestBrand,
  RawWPRestBrandsResponse,
} from "../../../../types/index";

import type {
  TransformedWPCategory,
  GetWPCategoriesResult,
  TransformedWPTag,
  GetWPTagsResult,
  TransformedWPBrand,
  GetBrandsResult,
} from "../types/productTaxonomyApiTypes";

// Get the WordPress REST API base URL from theme variables or use default fallback
const WP_REST_API_BASE =
  window.themeVariables?.wpRestApiBase || "/wp-json/wp/v2";

// --- Transformation Functions ---

export const taxonomyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Endpoint to fetch product categories from WordPress REST API
     */
    getCategories: builder.query<GetWPCategoriesResult, void>({
      query: () => ({
        url: `${WP_REST_API_BASE}/product_cat`,
        params: {
          per_page: 100,
        },
      }),
      transformResponse: (
        response: RawWPRestProductCategoriesResponse,
      ): GetWPCategoriesResult => {
        // 1. Transform each category into an array while preserving original order
        const transformedItems = response.map((category) =>
          transformCategoryData(category),
        );

        // 2. Create the ID-indexed map for lookups (like breadcrumbs)
        const categoriesById = transformedItems.reduce((acc, category) => {
          acc[category.id] = category;
          return acc;
        }, {} as Record<number, TransformedWPCategory>);

        // 3. Return both: 'items' preserves the order from the backend
        return {
          categories: categoriesById,
          items: transformedItems,
        };
      },
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({
                type: "Category" as const,
                id,
              })),
              { type: "Category" as const, id: "LIST" },
            ]
          : [{ type: "Category" as const, id: "LIST" }],
    }),

    /**
     * Endpoint to fetch product tags from WordPress REST API
     */
    getTags: builder.query<GetWPTagsResult, void>({
      query: () => ({
        url: `${WP_REST_API_BASE}/product_tag`,
        params: {
          per_page: 100,
        },
      }),
      transformResponse: (
        response: RawWPRestProductTagsResponse,
      ): GetWPTagsResult => {
        const tagsById = response.reduce((acc, tag) => {
          acc[tag.id] = transformTagData(tag);
          return acc;
        }, {} as Record<number, TransformedWPTag>);

        return { tags: tagsById };
      },
      providesTags: (result) =>
        result?.tags
          ? [
              ...Object.keys(result.tags).map((id) => ({
                type: "Tag" as const,
                id,
              })),
              { type: "Tag" as const, id: "LIST" },
            ]
          : [{ type: "Tag" as const, id: "LIST" }],
    }),

    /**
     * Fetches product brands from the WordPress REST API.
     */
    getBrands: builder.query<GetBrandsResult, void>({
      query: () => ({
        url: `${WP_REST_API_BASE}/product_brand`,
        params: { per_page: 100, orderby: "name", order: "asc" },
      }),
      transformResponse: (
        response: RawWPRestBrandsResponse,
      ): GetBrandsResult => {
        const rawBrands: RawWPRestBrand[] = Array.isArray(response)
          ? response
          : [];

        const brandsById = rawBrands.reduce((acc, brand) => {
          const transformedBrand: TransformedWPBrand = {
            id: brand.id,
            name: brand.name,
            slug: brand.slug,
            parent: brand.parent,
            description: brand.description,
            count: brand.count,
          };
          acc[brand.id] = transformedBrand;
          return acc;
        }, {} as GetBrandsResult["brands"]);

        return { brands: brandsById };
      },
      providesTags: (result) =>
        result?.brands
          ? [
              ...Object.keys(result.brands).map((id) => ({
                type: "Brand" as const,
                id,
              })),
              { type: "Brand" as const, id: "LIST" },
            ]
          : [{ type: "Brand" as const, id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

const transformCategoryData = (
  category: RawWPRestProductCategory,
): TransformedWPCategory => {
  const state = store.getState();
  const currentLanguage = state.language.currentLanguage;
  const defaultLanguage = state.language.defaultLanguage;

  let finalName = category.name;
  let finalDescription = category.description;

  if (
    currentLanguage !== defaultLanguage &&
    category.translations &&
    typeof category.translations === "object" &&
    category.translations[currentLanguage]
  ) {
    const translation = category.translations[currentLanguage];
    if (translation.name && translation.name.trim() !== "") {
      finalName = translation.name;
    }
    if (translation.description && translation.description.trim() !== "") {
      finalDescription = translation.description;
    }
  }

  return {
    id: category.id,
    name: finalName,
    slug: category.slug,
    parent: category.parent,
    count: category.count,
    description: finalDescription,
    link: category.link,
    taxonomy: category.taxonomy,
    thumbnail: category.thumbnail,
  };
};

const transformTagData = (tag: RawWPRestProductTag): TransformedWPTag => {
  const state = store.getState();
  const currentLanguage = state.language.currentLanguage;
  const defaultLanguage = state.language.defaultLanguage;

  const transformedTag = {
    id: tag.id,
    name: tag.name,
    slug: tag.slug,
    count: tag.count,
    description: tag.description,
    link: tag.link,
    taxonomy: tag.taxonomy,
    meta: tag.meta,
    acf: tag.acf,
  };

  if (
    currentLanguage !== defaultLanguage &&
    tag.translations &&
    typeof tag.translations === "object" &&
    tag.translations[currentLanguage]
  ) {
    const translation = tag.translations[currentLanguage];
    if (translation.name && translation.name.trim() !== "") {
      transformedTag.name = translation.name;
    }
    if (translation.description && translation.description.trim() !== "") {
      transformedTag.description = translation.description;
    }
  }

  return transformedTag;
};

export const {
  useGetCategoriesQuery,
  useGetTagsQuery,
  useGetBrandsQuery,
  util: { getRunningQueriesThunk },
} = taxonomyApi;

export const { getCategories, getTags, getBrands } = taxonomyApi.endpoints;
